import { useListState } from "@/hooks/use-list-state";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "wouter";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { PropertyGroupFilter } from "@/components/property-group-filter";
import { AlertCircle, Eye, PlayCircle, Settings2 } from "lucide-react";
import { searchIncludes } from "@/lib/search-normalize";
import { OverrideEditor } from "./_OverrideEditor";
import {
  SKIP_REASON_LABELS,
  formatDateTime,
  formatMoney,
  type CollectionPropertiesResponse,
  type CollectionPropertyRow,
  type CollectionsRunReport,
} from "./_shared";

// Properties tab (Task #4830) — the enable/disable surface the feature was
// missing. Collections runs ONLY for properties checked here, and only when the
// account master switch on the Settings tab is also on.

export function PropertiesTab({ readOnly, onGoToSettings }: { readOnly: boolean; onGoToSettings: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const search = useSearch();

  const [groupFilter, setGroupFilter] = useListState("collections:group", "all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<CollectionPropertyRow | null>(null);
  const [deepLinkApplied, setDeepLinkApplied] = useState(false);
  const [preview, setPreview] = useState<CollectionsRunReport | null>(null);

  const propertiesQuery = useQuery<CollectionPropertiesResponse>({
    queryKey: ["/api/app/collections/properties"],
  });

  const rows = propertiesQuery.data?.properties ?? [];
  const accountEnabled = propertiesQuery.data?.accountEnabled ?? false;

  // Deep-link target for the "Sent by Collections Rule" chip in a chat thread:
  // /app/financials/collections?tab=properties&property=<id>
  const deepLinkedPropertyId = useMemo(() => {
    const raw = new URLSearchParams(search).get("property");
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) ? n : null;
  }, [search]);

  // Open the editor once when arriving from the chat-bubble deep link. A
  // one-shot flag, not a derived `open` value — otherwise the sheet would
  // immediately reopen every time the user closed it.
  useEffect(() => {
    if (deepLinkApplied || deepLinkedPropertyId == null) return;
    const match = rows.find((r) => r.propertyId === deepLinkedPropertyId);
    if (match) {
      setEditing(match);
      setDeepLinkApplied(true);
    }
  }, [deepLinkApplied, deepLinkedPropertyId, rows]);

  const visible = useMemo(() => rows.filter((r) => {
    if (groupFilter !== "all" && r.propertyGroup !== groupFilter) return false;
    if (query.trim() && !searchIncludes(r.name, query) && !searchIncludes(r.address ?? "", query)) return false;
    return true;
  }), [rows, groupFilter, query]);

  const enabledCount = rows.filter((r) => r.enabled).length;
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.propertyId));

  const toggleMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ propertyId, enabled }: { propertyId: number; enabled: boolean }) => {
      const res = await apiRequest("PUT", `/api/app/collections/properties/${propertyId}`, { enabled });
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/app/collections/properties"] }),
    onError: (e: Error) => toast({
      title: "Couldn't update property",
      description: parseErrorMessage(e, "Couldn't change collections for that property. Please try again."),
      variant: "destructive",
    }),
  });

  const bulkMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiRequest("POST", "/api/app/collections/properties/bulk", {
        propertyIds: Array.from(selected),
        enabled,
      });
      return res.json() as Promise<{ changed: number }>;
    },
    onSuccess: (data, enabled) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/collections/properties"] });
      setSelected(new Set());
      toast({
        title: enabled ? "Collections enabled" : "Collections disabled",
        description: `Updated ${data.changed} propert${data.changed === 1 ? "y" : "ies"}.`,
      });
    },
    onError: (e: Error) => toast({
      title: "Bulk update failed",
      description: parseErrorMessage(e, "Couldn't update those properties. Please try again."),
      variant: "destructive",
    }),
  });

  const previewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/collections/preview", {});
      return res.json() as Promise<CollectionsRunReport>;
    },
    onSuccess: (report) => setPreview(report),
    onError: (e: Error) => toast({
      title: "Preview failed",
      description: parseErrorMessage(e, "Couldn't build the collections preview. Please try again."),
      variant: "destructive",
    }),
  });

  const runMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/collections/run", {});
      return res.json() as Promise<CollectionsRunReport>;
    },
    onSuccess: (report) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/collections/activity"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/collections/properties"] });
      setPreview(null);
      if (report.status === "account_disabled") {
        toast({
          title: "Collections is turned off",
          description: "Turn on the master switch in Settings before running collections.",
          variant: "destructive",
        });
        return;
      }
      if (report.status === "no_enabled_properties") {
        toast({
          title: "No properties enabled",
          description: "Check at least one property before running collections.",
        });
        return;
      }
      toast({
        title: "Collections run complete",
        description: `${report.noticed} notice(s) sent, ${report.skipped} skipped, ${report.failed} failed across ${report.propertiesProcessed} propert${report.propertiesProcessed === 1 ? "y" : "ies"}.`,
      });
    },
    onError: (e: Error) => toast({
      title: "Run failed",
      description: parseErrorMessage(e, "Couldn't run collections. Please try again."),
      variant: "destructive",
    }),
  });

  return (
    <div data-testid="collections-properties-tab">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Properties</h2>
          <p className="mt-1 text-sm text-gray-500">
            Collections only runs at properties you enable here. Each one can override the account rules.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => previewMutation.mutate()}
            disabled={previewMutation.isPending}
            className="flex items-center gap-2"
            data-testid="button-collections-preview"
          >
            <Eye className="h-4 w-4" />
            {previewMutation.isPending ? "Building..." : "Preview"}
          </Button>
          <Button
            onClick={() => runMutation.mutate()}
            disabled={readOnly || runMutation.isPending}
            className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white"
            data-testid="button-process-collections"
            data-tour="collections-process"
          >
            <PlayCircle className="h-4 w-4" />
            {runMutation.isPending ? "Running..." : "Run now"}
          </Button>
        </div>
      </div>

      {/* Enabling a property does nothing while the master switch is off, so
          say so explicitly rather than letting runs silently no-op. */}
      {!accountEnabled && enabledCount > 0 && (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          data-testid="banner-master-switch-off"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Collections is turned off for this account.</p>
            <p className="mt-0.5">
              {enabledCount} propert{enabledCount === 1 ? "y is" : "ies are"} enabled, but nothing will send until you
              turn on the master switch.{" "}
              <button type="button" className="underline" onClick={onGoToSettings} data-testid="link-goto-settings">
                Go to Settings
              </button>
            </p>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2" data-tour="collections-filters">
        <Input
          placeholder="Search properties..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9 w-56"
          data-testid="input-property-search"
        />
        {/* Groups carry an ownerId, so filtering by group is how you look at
            every property belonging to one owner. */}
        <PropertyGroupFilter properties={rows} value={groupFilter} onChange={setGroupFilter} />
        <span className="text-sm text-gray-500" data-testid="text-enabled-count">
          {enabledCount} of {rows.length} enabled
        </span>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2" data-testid="bulk-bar">
          <span className="text-sm text-gray-700">{selected.size} selected</span>
          <Button
            size="sm"
            variant="outline"
            disabled={readOnly || bulkMutation.isPending}
            onClick={() => bulkMutation.mutate(true)}
            data-testid="button-bulk-enable"
          >
            Enable
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={readOnly || bulkMutation.isPending}
            onClick={() => bulkMutation.mutate(false)}
            data-testid="button-bulk-disable"
          >
            Disable
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="button-bulk-clear">
            Clear
          </Button>
        </div>
      )}

      {preview && (
        <div className="mb-4 rounded-lg border border-gray-200 p-4" data-testid="preview-report">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Preview — {preview.noticed} member{preview.noticed === 1 ? "" : "s"} would be contacted
            </h3>
            <Button size="sm" variant="ghost" onClick={() => setPreview(null)} data-testid="button-preview-dismiss">
              Dismiss
            </Button>
          </div>
          <p className="mb-3 text-xs text-gray-500">
            Nothing has been sent. {preview.skipped} skipped across {preview.propertiesProcessed} enabled propert
            {preview.propertiesProcessed === 1 ? "y" : "ies"}.
          </p>
          {preview.decisions.length === 0 ? (
            <p className="text-sm text-gray-400">No members matched the current rules.</p>
          ) : (
            <ul className="max-h-64 space-y-1 overflow-y-auto text-sm">
              {preview.decisions.map((d) => (
                <li key={`${d.propertyId}-${d.memberId}`} className="flex items-center gap-2" data-testid={`preview-row-${d.memberId}`}>
                  <Badge variant={d.outcome === "sent" ? "default" : "outline"}>
                    {d.outcome === "sent" ? "Would send" : SKIP_REASON_LABELS[d.skipReason ?? ""] ?? "Skipped"}
                  </Badge>
                  <span className="font-medium text-gray-900">{d.memberName}</span>
                  <span className="text-gray-500">{d.propertyName ?? "—"}</span>
                  <span className="text-red-600">{formatMoney(d.amountDue)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200" data-tour="collections-properties-list">
        <table className="w-full text-sm" data-testid="properties-table">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="w-10 px-4 py-3">
                <Checkbox
                  checked={allVisibleSelected}
                  onCheckedChange={(checked) => {
                    setSelected(checked ? new Set(visible.map((r) => r.propertyId)) : new Set());
                  }}
                  aria-label="Select all visible properties"
                  data-testid="checkbox-select-all"
                />
              </th>
              <th className="w-16 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">On</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Property</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Group</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Rules</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last run</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {propertiesQuery.isLoading ? (
              <tr><td colSpan={7} className="px-4 py-6"><Skeleton className="h-6 w-full" /></td></tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <AlertCircle className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                  <p className="text-sm text-gray-400">
                    {rows.length === 0 ? "No properties yet." : "No properties match these filters."}
                  </p>
                </td>
              </tr>
            ) : visible.map((row) => {
              const customCount = Object.values(row.resolved.sources).filter((s) => s === "override").length;
              return (
                <tr key={row.propertyId} data-testid={`row-property-${row.propertyId}`}>
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selected.has(row.propertyId)}
                      onCheckedChange={(checked) => {
                        const next = new Set(selected);
                        if (checked) next.add(row.propertyId); else next.delete(row.propertyId);
                        setSelected(next);
                      }}
                      aria-label={`Select ${row.name}`}
                      data-testid={`checkbox-select-${row.propertyId}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={row.enabled}
                      disabled={readOnly || toggleMutation.isPending}
                      onCheckedChange={(checked) =>
                        toggleMutation.mutate({ propertyId: row.propertyId, enabled: checked === true })}
                      aria-label={`Enable collections for ${row.name}`}
                      data-testid={`checkbox-collections-enabled-${row.propertyId}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{row.name}</div>
                    {row.address && <div className="text-xs text-gray-500">{row.address}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{row.propertyGroup ?? "—"}</td>
                  <td className="px-4 py-3">
                    {customCount > 0
                      ? <Badge variant="secondary" data-testid={`badge-custom-${row.propertyId}`}>{customCount} custom</Badge>
                      : <Badge variant="outline" data-testid={`badge-inherited-${row.propertyId}`}>Inherited</Badge>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">{formatDateTime(row.lastRunAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing(row)}
                      className="flex items-center gap-1"
                      data-testid={`button-customize-${row.propertyId}`}
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                      Customize
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <OverrideEditor
        property={editing}
        open={editing != null}
        onOpenChange={(open) => { if (!open) setEditing(null); }}
        readOnly={readOnly}
      />
    </div>
  );
}
